<script lang="ts">
  import { createContext } from 'reka-ui'

  export type ModalTypes = 'dialog' | 'drawer'

  export type ModalState = Ref<unknown> | undefined

  export type ModalCheckerContext = {
    modalType: ModalTypes
    isOpen: Ref<boolean | undefined>
  }

  const [_useModalContext, provideModalChecker] =
    createContext<ModalCheckerContext>('gs-modal-checker')

  /** Used to check the type of the modal. Useful in for determining subcomponent's styles. */
  export function useModalContext(): ModalCheckerContext {
    try {
      return _useModalContext()
    } catch {
      return {
        modalType: 'drawer',
        isOpen: ref(false)
      }
    }
  }
</script>

<script setup lang="ts">
  import { useUserPreferences } from '../../composables/useUserPreferences'
  import { globalUtils } from '@gat-ship/utils'
  import ModalDialog from './ModalDialog.vue'
  import {
    computed,
    onMounted,
    reactive,
    ref,
    type Ref,
    type HTMLAttributes
  } from 'vue'
  import ModalDrawer from './ModalDrawer.vue'

  // Every field on ModalProps is forwarded to the inner ModalDialog / ModalDrawer via `h(component, props)` in the `component` computed below. `vue/no-unused-properties` can't see through `h()`'s dynamic spread, so each field that isn't also referenced directly in modal.vue's own template gets a per-line disable.
  export interface ModalProps {
    class?: HTMLAttributes['class']
    type?: ModalTypes
    maxWidth?: number
    /** Forwarded to inner ModalDialog / ModalDrawer via `h(component, props)`. */
    // eslint-disable-next-line vue/no-unused-properties
    loading?: boolean
    hideCloseButton?: boolean
    fullScreenMobile?: boolean
    /** Stretch the dialog to fill the whole viewport (dialog type only). */
    fullScreen?: boolean
  }

  defineOptions({ name: 'GatModal' })

  const props = defineProps<ModalProps>()

  const hasEvent = reactive({ clickCancel: false, clickSave: false })

  onMounted(() => {
    hasEvent.clickCancel =
      globalUtils.functions.components.hasEmitListener('click:cancel')
    hasEvent.clickSave =
      globalUtils.functions.components.hasEmitListener('click:save')
  })

  const isOpen = defineModel<boolean>('open', { required: false })

  const getUserPreferences = useUserPreferences()

  provideModalChecker({
    modalType: props.type ?? getUserPreferences().modalType ?? 'drawer',
    isOpen
  })

  const component = computed(() => {
    const type = props.type ?? getUserPreferences().modalType ?? 'drawer'
    return type === 'dialog' ? ModalDialog : ModalDrawer
  })

  function showModal() {
    isOpen.value = true
  }

  /** Will close the modal.
   * @param options.resetState - Whether to reset the state of the modal. @default true
   */
  function closeModal() {
    isOpen.value = false
  }

  defineExpose({
    showModal,
    closeModal
  })
</script>
<template>
  <component
    :is="component"
    ref="modal"
    v-model:open="isOpen"
    :max-width="props.maxWidth"
    :class="props.class"
    :hide-close-button="props.hideCloseButton"
    :full-screen-mobile="props.fullScreenMobile"
    :full-screen="props.fullScreen"
  >
    <slot />
  </component>
</template>
